import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { generatePrice } from "../../../utils";
import Image03 from "../../../assets/images/prints/money_03.svg";
import solarImage from "../../../assets/images/prints/solar-image.png";
import api from "../../../services/api";
import { Backdrop } from "../../../components";
import { useAuth } from "../../../contexts/AuthContext";

import { Container, Page, FieldButtons } from "./styles";

import { formatPhoneBR } from "../../../utils/mask";
import { isValidEmail } from "../../../utils/validations";

export default function Users(props) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listElectricCompanies, setListElectricCompanies] = useState([]);
  const [showPrice, setShowPrice] = useState(false);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [voltageLevel, setVoltageLevel] = useState("BT");
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
    concessionaria: "",
    total_project_power: "",
    price: "",
    descont: 0,
    type: "",
    observation: "",
  });

  const isSolar = useMemo(() => data.type === "SOLARY_ENERGY", [data.type]);
  const isPmuc = useMemo(() => data.type === "PMUC", [data.type]);
  const isAirSub = useMemo(() => data.type === "AIR_SUBSTATION", [data.type]);

  const cleanPhone = (value) => String(value || "").replace(/\D/g, "");

  const getElectricCompanies = async (uf) => {
    if (!uf) return;
    setShowBackdrop(true);
    try {
      const res = await api.get(`/dealers/state/${uf}`);
      setListElectricCompanies(res.data || []);
    } catch (error) {
      console.error("Error: ", error?.response?.data || error?.message);
    } finally {
      setShowBackdrop(false);
    }
  };

  const getValuePrice = (total_project_power) => {
    const price = generatePrice(total_project_power);
    if (!price) {
      toast.warn(`Valor "${total_project_power}" não tabelado. Entre em contato.`);
      return null;
    }
    setData((prev) => ({ ...prev, price: price ?? 0 }));
    setShowPrice(true);
    return price;
  };

  const validateCommonFields = () => {
    if (!data.name.trim()) {
      toast.warn("Informe o nome.");
      return false;
    }
    if (!isValidEmail(data.email)) {
      toast.warn("Informe um e-mail válido.");
      return false;
    }
    const phoneDigits = cleanPhone(data.phone);
    if (phoneDigits.length < 10) {
      toast.warn("Informe um telefone válido.");
      return false;
    }
    if (!data.type) {
      toast.warn("Selecione o tipo de projeto.");
      return false;
    }
    return true;
  };

  const validateSolarFields = () => {
    if (!data.state) {
      toast.warn("Selecione o estado.");
      return false;
    }
    if (!data.concessionaria) {
      toast.warn("Selecione a concessionária.");
      return false;
    }
    const power = Number(data.total_project_power);
    if (!power || power <= 0) {
      toast.warn("Potência total do projeto não pode ser 0kWp.");
      return false;
    }
    return true;
  };

  const submitInfor = async (event) => {
    event.preventDefault();
    if (!validateCommonFields()) return;
    
    setLoading(true);

    const dealer = listElectricCompanies.find((a) => a.name === data.concessionaria);

    if (isSolar) {
      if (!validateSolarFields()) return;
      const valor = getValuePrice(data.total_project_power);
      if (!valor) return;

      if (!dealer) {
        toast.warn("Concessionária inválida para o estado selecionado.");
        return;
      }

      const payload = {
        type: data.type,
        dealer_id: dealer.id,
        power: data.total_project_power,
        price: parseFloat(valor),
        user_id: user?.id,
        full_name: data.name || null,
        email: data.email || null,
        phone_number: cleanPhone(data.phone) || null,
        observation: data.observation || null,
        voltage_level: voltageLevel,
      };

      try {
        await api.post("budgeting", payload);
        toast.success("Proposta de orçamento enviada!");
      } catch (error) {
        console.error(error?.response?.data?.message || error.message);

        toast.error("Ocorreu um erro, por favor tente novamente")
      }

      setLoading(false);
      return;
    }

    if (isPmuc || isAirSub) {
      if (!data.observation.trim()) {
        toast.warn("Descreva a observação do projeto.");
        return;
      }

      const payload = {
        type: data.type,
        dealer_id: dealer.id,
        user_id: user?.id,
        full_name: data.name || null,
        email: data.email || null,
        phone_number: cleanPhone(data.phone) || null,
        observation: data.observation,
        price: null
      };

      try {
        await api.post("budgeting", payload);
        toast.success("A solicitação de orçamento foi enviada!");

        setTimeout(() => {
          navigate("/")
        }, 3000)
      } catch (error) {
        console.error(error?.response?.data?.message || error.message);

        toast.error("Ocorreu um erro, por favor tente novamente")
      }

      setLoading(false);
      return;
    }

    
    toast.info("Tipo de projeto enviado.");
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "type") {
      setShowPrice(false);
      setData((prev) => ({
        ...prev,
        type: value,
        ...(value !== '"SOLARY_ENERGY"'
          ? { state: "", concessionaria: "", total_project_power: "", price: "" }
          : {}),
      }));
      return;
    }

    if (name === "total_project_power") {
      const num = Number(value);
      if (num < 0) return;
      if (num > 1000) {
        toast.warn("Valor máximo de 1000kWp");
        return;
      }
      if (num <= 40) setVoltageLevel("BT");
      setData((prev) => ({ ...prev, total_project_power: value }));
      return;
    }

    if (name === "state") {
      setData((prev) => ({ ...prev, state: value, concessionaria: "" }));
      getElectricCompanies(value);
      return;
    }

    if (name === "phone") {
      const masked = formatPhoneBR(value);
      setData((prev) => ({ ...prev, phone: masked }));
      return;
    }

    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSetMiddlePower = (event) => {
    setVoltageLevel(event.target.value);
  };

  return (
    <Container>
      <Page>
        <header>
          <h1>Orçamento</h1>
          <div className="div__divider" />
          <span>Faça um orçamento instantaneamente.</span>
        </header>

        <div className="conteudo">
          <div
            className="div__multiColumn"
            style={{ margin: "auto", maxWidth: "1100px", flexWrap: "wrap" }}
          >
            <div className="div__multiColumn">
              <img src={Image03} alt="img_orcamento" className="img_orcamento" />
            </div>

            <div className="div__oneRow">
              <form onSubmit={submitInfor}>
                <div
                  style={{
                    maxWidth: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div className="div__multiColumn">
                    <input
                      type="text"
                      name="name"
                      placeholder="Nome"
                      className="input-custom-style"
                      value={data.name}
                      onChange={onInputChange}
                    />
                  </div>

                  <div className="div__multiColumn">
                    <input
                      type="email"
                      name="email"
                      placeholder="E-mail"
                      className="input-custom-style"
                      value={data.email}
                      onChange={onInputChange}
                    />
                  </div>

                  <div className="div__multiColumn">
                    <input
                      type="text"
                      name="phone"
                      placeholder="Telefone"
                      className="input-custom-style"
                      value={data.phone}
                      onChange={onInputChange}
                      inputMode="tel"
                    />
                  </div>

                  <div className="div__multiColumn">
                    <select
                      className="input-custom-style"
                      required
                      name="type"
                      value={data.type}
                      onChange={onInputChange}
                    >
                      <option value="" disabled hidden>
                        Tipo de projeto
                      </option>
                      <option value={"SOLARY_ENERGY"}>Energia Solar</option>
                      <option value={"PMUC"}>PMUC</option>
                      <option value={"AIR_SUBSTATION"}>Subestação Aérea</option>
                    </select>
                  </div>

                  <div className="div__multiColumn">
                    <select
                      className="input-custom-style"
                      required
                      name="state"
                      style={{ flex: 1 }}
                      value={data.state}
                      onChange={onInputChange}
                      disabled={showPrice}
                    >
                      <option value="" disabled>
                        Estado
                      </option>
                      <option value="AC">AC</option>
                      <option value="AL">AL</option>
                      <option value="AP">AP</option>
                      <option value="AM">AM</option>
                      <option value="BA">BA</option>
                      <option value="CE">CE</option>
                      <option value="DF">DF</option>
                      <option value="ES">ES</option>
                      <option value="GO">GO</option>
                      <option value="MA">MA</option>
                      <option value="MT">MT</option>
                      <option value="MS">MS</option>
                      <option value="MG">MG</option>
                      <option value="PA">PA</option>
                      <option value="PB">PB</option>
                      <option value="PR">PR</option>
                      <option value="PE">PE</option>
                      <option value="PI">PI</option>
                      <option value="RJ">RJ</option>
                      <option value="RN">RN</option>
                      <option value="RS">RS</option>
                      <option value="RO">RO</option>
                      <option value="RR">RR</option>
                      <option value="SC">SC</option>
                      <option value="SP">SP</option>
                      <option value="SE">SE</option>
                      <option value="TO">TO</option>
                    </select>
                  </div>

                  <div className="div__multiColumn">
                    <select
                      className="input-custom-style"
                      required
                      disabled={showPrice}
                      name="concessionaria"
                      style={{ flex: 2 }}
                      value={data.concessionaria}
                      onChange={onInputChange}
                    >
                      {!data.concessionaria && (
                        <option value="" disabled>
                          Nome da concessionária
                        </option>
                      )}
                      {data.concessionaria && (
                        <option value={data.concessionaria} disabled>
                          {data.concessionaria}
                        </option>
                      )}
                      {listElectricCompanies.length > 0 &&
                        listElectricCompanies.map((concess, idx) => (
                          <option key={idx} value={concess.name}>
                            {concess.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {isSolar ? (
                    <>
                      <div className="div__multiColumn">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                          }}
                        >
                          <input
                            type="number"
                            min="0"
                            name="total_project_power"
                            value={data.total_project_power}
                            placeholder="Potência total do projeto"
                            className="input__number input-custom-style"
                            step="0.01"
                            style={{ flex: 1, width: "100%" }}
                            onChange={onInputChange}
                            disabled={showPrice}
                            required
                          />
                          <div className="pos_field">kWp</div>
                        </div>
                      </div>

                      <div className="div__multiColumn">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                          }}
                        >
                          <select
                            className="input-custom-style-budget"
                            disabled={
                              data.total_project_power < 41 ||
                              data.total_project_power > 75 ||
                              showPrice
                            }
                            name="middlePower"
                            value={voltageLevel}
                            onChange={handleSetMiddlePower}
                          >
                            <option value={"BT"}>Baixa tensão</option>
                            <option value={"MT"}>Média tensão</option>
                          </select>
                        </div>
                      </div>
                    </>
                  ) : (
                    (isPmuc || isAirSub) && (
                      <div className="div__multiColumn">
                        <textarea
                          name="observation"
                          placeholder="Observação do projeto"
                          value={data.observation}
                          onChange={onInputChange}
                          required
                        />
                      </div>
                    )
                  )}

                  <button
                    type="submit"
                    style={{ alignSelf: "center" }}
                    className={
                      showPrice && isSolar
                        ? "btn__finish_disabled primary-button-style"
                        : "primary-button-style"
                    }
                  >
                    {loading ? "ENVIANDO..." : `${isSolar ? "Realizar Orçamento" : "Enviar Solicitação"}`}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {isSolar && showPrice && (
            <div className="div__image_solar">
              <img src={solarImage} alt="panel_solar_image" />
            </div>
          )}

          {isSolar && showPrice && (
            <div className="div__orcamento_field">
              <p>Seu projeto custará</p>
              <h2>R$ {data.price}</h2>

              <p>Garanta seu projeto hoje mesmo!</p>
              <br />

              <FieldButtons delete={props.delete}>
                <button
                  onClick={() => {
                    navigate("/")
                  }}
                  className="primary-button-style"
                  color="primary"
                >
                  Voltar
                </button>
                <button
                  onClick={() => setShowPrice(false)}
                  className="secondary-button-style"
                >
                  Refazer orçamento
                </button>
              </FieldButtons>
            </div>
          )}
        </div>
      </Page>
      {showBackdrop && <Backdrop />}
    </Container>
  );
}
